/**
 * The keyboard shortcuts, in one place.
 *
 * The single source the handler and the cheat sheet both read, so a shortcut
 * can never work while the help swears it does not exist, or the reverse. The
 * screen jumps come from the screen registry, so a new screen gets a key and a
 * line here without either list being edited, and a screen this user cannot
 * open is not offered, because a shortcut offered to someone who cannot use it
 * is a worse help than none.
 */

import { canOpenScreen, screenHotkeys, type ScreenAccess, type ScreenEntry } from './screens';

export interface Shortcut {
  keys: string;
  label: string;
}

const LEADING: Shortcut[] = [
  { keys: 'Ctrl / ⌘ K', label: 'Open command search' },
  { keys: '?', label: 'Show this shortcut sheet' },
  { keys: '`', label: 'Hide the panels for a clean map' },
];

const TRAILING: Shortcut[] = [{ keys: 'Esc', label: 'Close search or this sheet' }];

/** The number keys this user may press, and where each one goes. */
export function screenJumps(screens: ScreenEntry[], access: ScreenAccess): Array<{ key: string; screen: ScreenEntry }> {
  return screenHotkeys(screens).filter(({ screen }) => canOpenScreen(screen, access));
}

export function visibleShortcuts(screens: ScreenEntry[], access: ScreenAccess): Shortcut[] {
  const jumps = screenJumps(screens, access).map(({ key, screen }) => ({
    keys: key,
    label: `Go to ${screen.label}`,
  }));
  return [...LEADING, ...jumps, ...TRAILING];
}
